import { createBrowserRouter, Outlet } from 'react-router-dom';
import WhatsAppRouteGuard from '../components/WhatsApp/WhatsAppRouteGuard';

// ADMIN
import ActivityLog from '../components/Admin/ActivityLog';
import AdminDashboard from '../components/Admin/AdminDashboard';
import AdminManagement from '../components/Admin/AdminManagement';
import AdminSmsAnalytics from '../components/Admin/AdminSmsAnalytics';
import Blacklist from '../components/Admin/Blacklist';
import Credit from '../components/Admin/Credit';
import SMSGatewayRoutes from '../components/Admin/GatewayRoutes';
import GlobalRates from '../components/Admin/GlobalRates';
import SMSCampaign from '../components/Admin/SMSCampaign';
import SmsTraffic from '../components/Admin/SmsTraffic';
import SMSTransactionReport from '../components/Admin/SMSTransactionReport';
import SpamReport from '../components/Admin/SpamReport';
import UserManagement from '../components/Admin/UserManagement';
import UserRates from '../components/Admin/UserRates';
import WhatsAppManagerManagement from '../components/Admin/WhatsAppManagerManagement';
import WhatsAppPricingRates from '../components/Admin/WhatsAppPricingRates';
import ManagerDashboard from '../components/Manager/ManagerDashboard';
import ManagerSMSCampaign from '../components/Manager/ManagerSMSCampaign';
import ManagerTransferCreditHistory from '../components/Manager/ManagerTransferCreditHistory';
import ManagerTransferCredits from '../components/Manager/ManagerTransferCredits';
import ManagerUserManagement from '../components/Manager/ManagerUserManagement';

// Layouts
import { LinkedInCallback } from 'react-linkedin-login-oauth2';
import Page404 from '../components/shared/Page404/Page404';
import AccountActivation from '../layouts/Authenticate/AccountActivation';
import Login from '../layouts/Authenticate/Login';
import PhoneVerification from '../layouts/Authenticate/PhoneVerification';
import Registration from '../layouts/Authenticate/Registration';
import RequestPasswordReset from '../layouts/Authenticate/RequestPasswordReset';
import ResetPassword from '../layouts/Authenticate/ResetPassword';
import ResetPasswordSuccess from '../layouts/Authenticate/ResetPasswordSuccess';
import PrivacyPolicy from '../layouts/StaticPages/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from '../layouts/StaticPages/TermsOfService/TermsOfService';

// Dashboard routes
import Sidebar from '../components/Sidebar/Sidebar';
import Topbar from '../components/Topbar/Topbar';
import SuspendAccount from '../layouts/Authenticate/SuspendAccount';
import BuyCredit from '../layouts/BuyCredit/BuyCredit';
import Contact from '../layouts/Contact/Contact';
import ContactOurExperts from '../layouts/Contact/ContactOurExperts';
import CampaignAnalytics from '../layouts/Dashboard/CampaignAnalytics/SmsAnalyitics';
import Dashboard from '../layouts/Dashboard/Dashboard';
import SMSPriceList from '../layouts/SMSPriceList/SMSPriceList';
import EmailAPI from '../layouts/StaticPages/EmailAPI';
import {
  GettingStarted,
  NumberDetails,
  TemplateCreator,
  TemplateEditor,
  WhatsAppApiDoc,
  WhatsAppNew,
  WhatsAppSandbox,
  WhatsAppSuccess,
} from '../layouts/WhatsApp';
import AgentInvitationResponse from '../layouts/WhatsApp/components/agents/AgentInvitationResponse';
// Account Settings
import APIMessage from '../layouts/AccountSetting/APIMessage/APIMessage';
import BillingLog from '../layouts/AccountSetting/BillingLog/BillingLog';
import SidebarAccount from '../layouts/AccountSetting/UserProfile/sidebar';
import UserProfile from '../layouts/AccountSetting/UserProfile/UserProfile';
import UserSecurity from '../layouts/AccountSetting/UserSecurity';

// Campaign
import GlobalAlert from '../components/GlobalAlert';
import CreateCampaign from '../layouts/Campaign/CreateCampaign/CreateCampaign';
import CampaignOverview from '../layouts/Campaign/Overview/CampaignOverview';

const all_routes = [
  { path: '/', element: <Login /> },
  { path: '/login', element: <Login /> },
  { path: '/signup', element: <Registration /> },
  { path: '/linkedin', element: <LinkedInCallback /> },
  { path: '/request-forgot-password', element: <RequestPasswordReset /> },
  { path: '/email/verify/:token', element: <AccountActivation /> },
  { path: '/phone/verify/:token', element: <PhoneVerification /> },
  { path: '/password-reset-success', element: <ResetPasswordSuccess /> },
  { path: '/password-reset/:token', element: <ResetPassword /> },
  { path: '/terms-of-service', element: <TermsOfService /> },
  { path: '/privacy-policy', element: <PrivacyPolicy /> },
  { path: '/account-suspended', element: <SuspendAccount /> },
  { path: '/whatsapp/agent/accept-invitation/:token', element: <AgentInvitationResponse action="accept" /> },
  { path: '/whatsapp/agent/reject-invitation/:token', element: <AgentInvitationResponse action="reject" /> },
  // { path: '/dashboard/sms-price-list', element: <SMSPriceList /> },
  { path: '*', element: <Page404 /> },
  {
    path: '/dashboard',
    element: (
      <>
        <GlobalAlert />
        <Topbar />
        <div className="Dashboard">
          <Sidebar />
          <Outlet />
        </div>
      </>
    ),
    children: [
      { path: '/dashboard', element: <Dashboard /> },
      { path: '/dashboard/contact', element: <Contact /> },
      { path: '/dashboard/contact-our-expert', element: <ContactOurExperts /> },
      { path: '/dashboard/payment/buy-credits', element: <BuyCredit /> },
      { path: '/dashboard/campaign-analytics', element: <CampaignAnalytics /> },
      {
        path: '/dashboard/whatsapp',
        element: (
          <WhatsAppRouteGuard>
            <WhatsAppNew />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/dashboard/whatsapp/getting-started',
        element: (
          <WhatsAppRouteGuard>
            <GettingStarted />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/dashboard/whatsapp/api-doc',
        element: (
          <WhatsAppRouteGuard>
            <WhatsAppApiDoc />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/dashboard/whatsapp/sandbox',
        element: (
          <WhatsAppRouteGuard>
            <WhatsAppSandbox />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/dashboard/whatsapp/phone-number-details/:waba_id/:phone_number_id',
        element: (
          <WhatsAppRouteGuard>
            <NumberDetails />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/dashboard/whatsapp/phone-number-details/:waba_id/:phone_number_id/templates/create',
        element: (
          <WhatsAppRouteGuard>
            <TemplateCreator />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/dashboard/whatsapp/phone-number-details/:waba_id/:phone_number_id/templates/:template_id',
        element: (
          <WhatsAppRouteGuard>
            <TemplateEditor />
          </WhatsAppRouteGuard>
        )
      },
      { path: '/dashboard/payment/whatsapp-success', element: <WhatsAppSuccess /> },
      { path: '/dashboard/email-api-home', element: <EmailAPI /> },
      { path: '/dashboard/sms-price-list', element: <SMSPriceList /> },
      { path: '/dashboard/account/api-message', element: <APIMessage /> },
      {
        path: '/dashboard/account',
        element: (
          <div className="AccountSettings">
            <SidebarAccount />
            <Outlet />
          </div>
        ),
        children: [
          { path: '/dashboard/account/personal-info', element: <UserProfile /> },
          { path: '/dashboard/account/user-security', element: <UserSecurity /> },
        ],
      },
      {
        element: (
          <div className="AccountSettings">
            <Outlet />
          </div>
        ),
        children: [
          { path: '/dashboard/account/billing-log', element: <BillingLog /> },
        ],
      },
      { path: '/dashboard/campaign/overview', element: <CampaignOverview /> },
      {
        path: '/dashboard/campaign/campaign-create',
        element: <CreateCampaign />,
      },
    ],
  },
  {
    path: '/admin/dashboard',
    element: <AdminDashboard />,
    children: [
      { path: '/admin/dashboard', element: <SmsTraffic /> },
      { path: '/admin/dashboard/sms-analytics', element: <AdminSmsAnalytics /> },
      { path: '/admin/dashboard/transfer-credit', element: <Credit /> },
      { path: '/admin/dashboard/transfer-credit-history', element: <ManagerTransferCreditHistory /> },
      { path: '/admin/dashboard/global-rates', element: <GlobalRates /> },
      { path: '/admin/dashboard/admin-management', element: <AdminManagement /> },
      { path: '/admin/dashboard/user-management', element: <UserManagement /> },
      { path: '/admin/dashboard/whatsapp-manager-management', element: <WhatsAppManagerManagement /> },
      { path: '/admin/dashboard/whatsapp-pricing-rates', element: <WhatsAppPricingRates /> },
      { path: '/admin/dashboard/sms-campaign', element: <SMSCampaign /> },
      { path: '/admin/dashboard/sms-transaction-report', element: <SMSTransactionReport /> },
      { path: '/admin/dashboard/user-rates', element: <UserRates /> },
      { path: '/admin/dashboard/spam-report', element: <SpamReport /> },
      { path: '/admin/dashboard/blacklist', element: <Blacklist /> },
      { path: '/admin/dashboard/sms-gateway-routes', element: <SMSGatewayRoutes /> },
      { path: '/admin/dashboard/activity-log', element: <ActivityLog /> },

    ]
  },
  {
    path: '/manager/dashboard',
    element: <ManagerDashboard />,
    children: [
      { path: '/manager/dashboard', element: <AdminSmsAnalytics /> },
      { path: '/manager/dashboard/transfer-credit', element: <ManagerTransferCredits /> },
      { path: '/manager/dashboard/transfer-credit-history', element: <ManagerTransferCreditHistory /> },
      { path: '/manager/dashboard/user-management', element: <ManagerUserManagement /> },
      { path: '/manager/dashboard/sms-analytics', element: <AdminSmsAnalytics /> },
      { path: '/manager/dashboard/sms-campaign', element: <ManagerSMSCampaign /> },
      { path: '/manager/dashboard/sms-transaction-report', element: <SMSTransactionReport /> },
      { path: '/manager/dashboard/sms-price-list', element: <SMSPriceList /> },
      { path: '/manager/dashboard/payment/buy-credits', element: <BuyCredit /> },
      {
        path: '/manager/dashboard/whatsapp',
        element: (
          <WhatsAppRouteGuard>
            <WhatsAppNew />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/manager/dashboard/whatsapp/getting-started',
        element: (
          <WhatsAppRouteGuard>
            <GettingStarted />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/manager/dashboard/whatsapp/sandbox',
        element: (
          <WhatsAppRouteGuard>
            <WhatsAppSandbox />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/manager/dashboard/whatsapp/phone-number-details/:waba_id/:phone_number_id',
        element: (
          <WhatsAppRouteGuard>
            <NumberDetails />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/manager/dashboard/whatsapp/phone-number-details/:waba_id/:phone_number_id/templates/create',
        element: (
          <WhatsAppRouteGuard>
            <TemplateCreator />
          </WhatsAppRouteGuard>
        )
      },
      {
        path: '/manager/dashboard/whatsapp/phone-number-details/:waba_id/:phone_number_id/templates/:template_id',
        element: (
          <WhatsAppRouteGuard>
            <TemplateEditor />
          </WhatsAppRouteGuard>
        )
      },
      { path: '/manager/dashboard/payment/whatsapp-success', element: <WhatsAppSuccess /> },
      {
        path: '/manager/dashboard/account',
        element: (
          <div className="AccountSettings" style={{ height: '100%' }}>
            <SidebarAccount />
            <Outlet />
          </div>
        ),
        children: [
          { path: '/manager/dashboard/account/personal-info', element: <UserProfile /> },
          { path: '/manager/dashboard/account/user-security', element: <UserSecurity /> },
        ],
      },
      {
        element: (
          <div className="AccountSettings" style={{ height: '100%' }}>
            <Outlet />
          </div>
        ),
        children: [
          { path: '/manager/dashboard/account/billing-log', element: <BillingLog /> },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(all_routes);

export default router;
